import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AdminOrderDetailResponse, LegMethod, PickupDeliveryResponse, formatScheduled } from '../../services/ordersApi';
import { providerSettlementsApi } from '../../services/providerSettlementsApi';
import { ProviderTrip } from '../../types/providerSettlements';
import { useAuth } from '../../hooks/useAuth';
import Money from '../finance/Money';
import { customerDisclaimer } from '../../utils/insuranceDisclaimer';
import { transportLabel, TransportType } from '../../types/delivery';

type LegKind = 'pickup' | 'return';

const METHOD_LABELS: Record<LegKind, Record<LegMethod, string>> = {
  pickup: { GARAGAT: 'Garagat picks up', SELF: 'Customer brings the car' },
  return: { GARAGAT: 'Garagat delivers', SELF: 'Customer collects' },
};

/**
 * "Garagat picks up" / "Customer brings the car" (and the return pair); null when the method is unknown.
 * #1254 — a Garagat leg of a Zofeur order reads "Zofeur driver" both ways: it is not a recovery truck.
 */
export function legMethodLabel(
  kind: LegKind,
  method: LegMethod | null | undefined,
  transportType?: TransportType | null,
): string | null {
  if (method !== 'GARAGAT' && method !== 'SELF') return null;
  if (method === 'GARAGAT' && transportType === 'ZOFEUR') return transportLabel('ZOFEUR');
  return METHOD_LABELS[kind][method];
}

/**
 * #641 — whether a leg can take a driver at all. A SELF leg never can (orders answers 409
 * "Transition not allowed for this order's delivery choice"), so its assign button must not exist.
 * An unknown method (an orders build that predates the field) keeps the button: guessing SELF would
 * hide a real action.
 */
export function legTakesDriver(method: LegMethod | null | undefined): boolean {
  return method !== 'SELF';
}

/**
 * Spec 072 v2 (AC-12) — "Zofeur driver" / "Recovery truck" on the order header and the legs panel.
 * Nothing for a null type: the order has no Garagat leg, so nothing moves the car.
 */
export function TransportBadge({ type, testId }: { type: TransportType | null | undefined; testId: string }) {
  const label = transportLabel(type);
  if (!label) return null;
  return (
    <span className={`admin-badge ${type === 'ZOFEUR' ? 'order-teal' : 'order-neutral'}`} data-testid={testId}>
      {label}
    </span>
  );
}

const LEG_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Driver assigned',
  EN_ROUTE: 'En route',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

function legStatusLabel(status: string): string {
  return LEG_STATUS_LABELS[status] ?? status.replace(/_/g, ' ').toLowerCase();
}

function Row({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <div className="admin-field" data-testid={testId}>
      <div className="admin-field-label">{label}</div>
      <div className="admin-field-value">{value ?? '—'}</div>
    </div>
  );
}

/**
 * The recorded-trip lookup for the order: `null` while not needed (no completed Garagat leg), then
 * loading → the trips merchant-settlement recorded, or unavailable (no `settlements` screen, or the
 * call failed). A trip's id is the orders leg id.
 *
 * `awaiting` — the completed legs with a provider whose trip is not recorded YET but may still be on
 * its way: merchant-settlement writes it from a Kafka event orders publishes after commit, so right
 * after "Mark complete" the row is normally a moment behind. Those legs read "Pending" and the panel
 * re-checks, instead of raising a false "not recorded" at exactly the moment an admin looks.
 */
type TripLookup =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'loaded'; trips: ProviderTrip[]; awaiting: string[] };

/** How long after a leg completes its trip may still legitimately be on its way. */
export const TRIP_RECORDING_GRACE_MS = 2 * 60_000;
/** Re-check cadence while a trip is awaited, and the cap on re-checks (~30 s) before saying "No". */
export const TRIP_RECHECK_MS = 3_000;
export const TRIP_MAX_RECHECKS = 10;

function completedRecently(leg: PickupDeliveryResponse, now: number): boolean {
  if (!leg.completedAt) return false;
  const at = Date.parse(leg.completedAt);
  return Number.isFinite(at) && now - at < TRIP_RECORDING_GRACE_MS;
}

function TripRecorded({ kind, leg, lookup }: { kind: LegKind; leg: PickupDeliveryResponse; lookup: TripLookup }) {
  let value: ReactNode;
  let trip: ProviderTrip | undefined;
  if (lookup.state === 'loading') value = 'Checking…';
  else if (lookup.state === 'unavailable') value = 'Unavailable';
  else if (lookup.state === 'loaded') {
    trip = lookup.trips.find((t) => t.tripId === leg.id);
    if (trip) value = 'Yes';
    else if (!leg.providerId) value = 'No — no recovery provider on this leg';
    else if (lookup.awaiting.includes(leg.id)) value = 'Pending — the trip is being recorded';
    else value = 'No — not recorded (the delivery fee may have been off when this leg completed)';
  } else {
    value = null;
  }
  return (
    <>
      <Row label="Trip recorded" value={value} testId={`leg-${kind}-trip`} />
      {trip && (
        // The cost frozen on the RECORDED trip — what a provider settlement will pay. Blank, not 0.00,
        // when the provider had no cost (AC-43).
        <Row
          label="Trip cost (frozen)"
          testId={`leg-${kind}-trip-cost`}
          value={<Money amount={trip.costPerTrip ?? null} glossaryKey="providerCostPerTrip" />}
        />
      )}
    </>
  );
}

function Leg({ kind, title, method, transportType, leg, lookup }: {
  kind: LegKind;
  title: string;
  method: LegMethod | null | undefined;
  transportType: TransportType | null | undefined;
  leg: PickupDeliveryResponse | undefined;
  lookup: TripLookup;
}) {
  const methodLabel = legMethodLabel(kind, method, transportType);
  const self = method === 'SELF';

  let status: string;
  if (leg) status = legStatusLabel(leg.status);
  else if (self) status = 'No driver — the customer handles this leg';
  else status = 'No driver assigned yet';

  return (
    <section data-testid={`leg-${kind}`} aria-label={title} style={{ marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>{title}</div>
      <div className="admin-detail-grid">
        <Row label="Method" value={methodLabel} testId={`leg-${kind}-method`} />
        <Row label="Status" value={status} testId={`leg-${kind}-status`} />
        {!self && (
          <>
            <Row label="Driver" value={leg?.driverName} />
            <Row label="Driver phone" value={leg?.driverPhone} />
            <Row label={transportType === 'ZOFEUR' ? 'Provider' : 'Recovery provider'} value={leg?.providerName} testId={`leg-${kind}-provider`} />
            {leg?.providerId && (
              // BR-26 — the cost frozen onto the leg at assignment is what its recorded trip carries
              // into a provider settlement. Blank (em dash) when the provider had no cost: never 0.00.
              <Row
                label="Provider cost (frozen)"
                testId={`leg-${kind}-cost`}
                value={<Money amount={leg.providerCostPerTrip ?? null} glossaryKey="providerCostPerTrip" />}
              />
            )}
            {leg && leg.providerShowDisclaimer && (
              // Spec 072 (AC-14) — the English text frozen onto the leg at assignment, as the customer
              // reads it (amount filled). A later registry edit does not change it.
              <Row
                label="Disclaimer shown to customer"
                testId={`leg-${kind}-disclaimer`}
                value={customerDisclaimer(leg.providerShowDisclaimer, leg.providerInsuredAmount, leg.providerDisclaimerEn)}
              />
            )}
            {leg?.completedAt && (
              <Row label="Completed" value={new Date(leg.completedAt).toLocaleString()} />
            )}
            {leg?.status === 'COMPLETED' && <TripRecorded kind={kind} leg={leg} lookup={lookup} />}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * #634 / #641 — each leg of the order (pickup, drop-off) as the admin needs it: who moves the car,
 * which driver and recovery provider, the leg's status and the provider cost frozen at assignment.
 *
 * A leg row only exists once a driver is assigned, so the method comes from the order-level
 * `pickupMethod` / `returnMethod`, never from the row. Whether a trip was *recorded* is
 * merchant-settlement's fact, not the order's: once a Garagat leg is COMPLETED the panel asks
 * `GET /api/admin/provider-settlements/order-trips` (the `settlements` screen) and matches the
 * trip to the leg by id.
 */
export default function OrderLegsPanel({ order }: { order: AdminOrderDetailResponse }) {
  const auth = useAuth();
  const pickup = order.pickupDeliveries?.find((d) => d.type === 'PICKUP');
  const dropoff = order.pickupDeliveries?.find((d) => d.type === 'RETURN_DELIVERY');
  const garagatLegs = [
    order.pickupMethod !== 'SELF' ? pickup : undefined,
    order.returnMethod !== 'SELF' ? dropoff : undefined,
  ].filter((l): l is PickupDeliveryResponse => !!l);
  const completed = garagatLegs.filter((l) => l.status === 'COMPLETED');
  // Changes whenever a leg completes (or its provider changes), so completing the SECOND leg
  // refetches too — a boolean "any completed" would already be true and never re-run the lookup.
  const completedKey = completed.map((l) => `${l.id}:${l.providerId ?? ''}`).join('|');
  const canSeeTrips = auth.hasScreen('settlements');
  const [lookup, setLookup] = useState<TripLookup>({ state: 'idle' });

  // The latest legs for the lookup below, which is keyed on `completedKey` alone; and every leg seen
  // still open on this page, so one the admin just completed is awaited even with a skewed clock.
  const completedRef = useRef<PickupDeliveryResponse[]>(completed);
  const seenOpenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    completedRef.current = completed;
    garagatLegs.filter((l) => l.status !== 'COMPLETED').forEach((l) => seenOpenRef.current.add(l.id));
  });

  useEffect(() => {
    if (!completedKey) { setLookup({ state: 'idle' }); return; }
    if (!canSeeTrips || !auth.token) { setLookup({ state: 'unavailable' }); return; }
    const token = auth.token;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let rechecks = 0;
    setLookup({ state: 'loading' });
    const check = () => {
      providerSettlementsApi.orderTrips(order.id, token)
        .then((trips) => {
          if (cancelled) return;
          const now = Date.now();
          const awaiting = rechecks < TRIP_MAX_RECHECKS
            ? completedRef.current
              .filter((l) => l.providerId && !trips.some((t) => t.tripId === l.id))
              .filter((l) => seenOpenRef.current.has(l.id) || completedRecently(l, now))
              .map((l) => l.id)
            : [];
          setLookup({ state: 'loaded', trips, awaiting });
          if (awaiting.length > 0) {
            rechecks += 1;
            timer = setTimeout(check, TRIP_RECHECK_MS);
          }
        })
        .catch(() => { if (!cancelled) setLookup({ state: 'unavailable' }); });
    };
    check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [completedKey, canSeeTrips, auth.token, order.id]);

  return (
    <div className="admin-card" data-testid="order-legs">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h3 className="admin-card-title" style={{ margin: 0 }}>Pickup &amp; Drop-off</h3>
        <TransportBadge type={order.transportType} testId="legs-transport-type" />
      </div>
      <div className="admin-detail-grid" style={{ marginBottom: '1rem' }}>
        {/* #1181 — the reserved day; the return leg has no stored date or slot yet (#1178). */}
        <Row label="Scheduled date" value={formatScheduled(order.scheduledDate)} />
        <Row label="Pickup time" value={order.pickupTimeSlotLabel} />
      </div>
      <Leg kind="pickup" title="Pickup" method={order.pickupMethod} transportType={order.transportType} leg={pickup} lookup={lookup} />
      <Leg kind="return" title="Drop-off" method={order.returnMethod} transportType={order.transportType} leg={dropoff} lookup={lookup} />
    </div>
  );
}
